/**
 * Video source URLs for cameras, DVRs and NVRs.
 *
 * There is no vendor-neutral protocol for "give me a stream" the way there is
 * for reachability (ping) or generic web access — every manufacturer bolts
 * RTSP and a snapshot CGI onto its own path convention. This is a best-effort
 * URL builder for the handful of conventions that cover most of what shows up
 * in a small/medium office (Hikvision, Dahua/Amcrest, Reolink, Axis), plus a
 * generic guess for everything else. It is meant to save an operator from
 * looking the path up, not to replace checking the camera's own manual when
 * the guess is wrong.
 */

export const CAMERA_VENDORS = ['generic', 'hikvision', 'dahua', 'amcrest', 'reolink', 'axis'] as const;
export type CameraVendor = (typeof CAMERA_VENDORS)[number];

export const CAMERA_VENDOR_LABELS: Record<CameraVendor, string> = {
  generic: 'Generic / ONVIF',
  hikvision: 'Hikvision',
  dahua: 'Dahua',
  amcrest: 'Amcrest',
  reolink: 'Reolink',
  axis: 'Axis',
};

/** Resource kinds this module applies to — see `plugins/office-devices/plugin.json`. */
const CAMERA_KINDS = new Set(['ip-camera', 'dvr-nvr']);

export function isCameraResource(resource: { kind: string }): boolean {
  return CAMERA_KINDS.has(resource.kind);
}

export function cameraVendorFor(resource: { config?: Record<string, unknown> }): CameraVendor {
  const raw = String(resource.config?.vendor ?? '').toLowerCase();
  return (CAMERA_VENDORS as readonly string[]).includes(raw) ? (raw as CameraVendor) : 'generic';
}

/** Which channel to build the stream/preview for — an NVR's `previewChannel`, or channel 1 for a single camera. */
export function cameraChannelFor(resource: { config?: Record<string, unknown> }): number {
  const raw = Number(resource.config?.previewChannel ?? 1);
  return Number.isInteger(raw) && raw > 0 ? raw : 1;
}

export interface CameraVideoPaths {
  /** Path (with query string where relevant) to request over RTSP. */
  rtspPath: string;
  /** Path (with query string where relevant) that returns one JPEG frame over HTTP. */
  snapshotPath: string;
  /** Set when the path is a generic guess rather than a documented vendor convention. */
  note: string | null;
}

/** Vendor-specific stream/snapshot paths for a 1-based channel number. */
export function cameraVideoPaths(vendor: CameraVendor, channel: number): CameraVideoPaths {
  const ch = channel > 0 ? Math.floor(channel) : 1;

  switch (vendor) {
    case 'hikvision': {
      // Channel IDs are "<channel><stream>", stream 01 = main stream.
      const streamId = `${ch}01`;
      return { rtspPath: `/Streaming/Channels/${streamId}`, snapshotPath: `/ISAPI/Streaming/channels/${streamId}/picture`, note: null };
    }
    case 'dahua':
    case 'amcrest':
      return {
        rtspPath: `/cam/realmonitor?channel=${ch}&subtype=0`,
        snapshotPath: `/cgi-bin/snapshot.cgi?channel=${ch}`,
        note: null,
      };
    case 'reolink': {
      const index = ch - 1;
      return {
        rtspPath: `/h264Preview_${String(ch).padStart(2, '0')}_main`,
        snapshotPath: `/cgi-bin/api.cgi?cmd=Snap&channel=${index}&rs=itr`,
        note: null,
      };
    }
    case 'axis':
      return { rtspPath: '/axis-media/media.amp', snapshotPath: '/axis-cgi/jpg/image.cgi', note: null };
    default:
      return {
        rtspPath: '/stream1',
        snapshotPath: '/snapshot.jpg',
        note: 'Generic path guess — pick the matching vendor above if you know it, or check the device manual if this does not connect.',
      };
  }
}

/** `rtsp://[user[:pass]@]host[:port]/path`, for pasting into VLC or another player. */
export function rtspDisplayUrl(host: string, port: number, path: string, username?: string | null, password?: string | null): string {
  const hostPart = host.includes(':') ? `[${host}]` : host;
  const portPart = port === 554 ? '' : `:${port}`;
  const auth = username ? `${encodeURIComponent(username)}${password ? `:${encodeURIComponent(password)}` : ''}@` : '';
  return `rtsp://${auth}${hostPart}${portPart}${path}`;
}
